import React, { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentUser, type MeResponse } from '../services/apiService';

type AuthState = {
  isLoaded: boolean;
  isSignedIn: boolean;
  user: MeResponse | null;
};

const AuthContext = createContext<AuthState>({
  isLoaded: false,
  isSignedIn: false,
  user: null,
});

export function AuthProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  // Always ask the backend who we are via /auth/me. In dev (AUTH_ENABLED=false)
  // the backend returns its DevelopmentUser, so identity and roles have a single
  // source of truth on the server — the frontend never fabricates a dev user.
  const [state, setState] = useState<AuthState>({
    isLoaded: false,
    isSignedIn: false,
    user: null,
  });

  useEffect(() => {
    let cancelled = false;

    getCurrentUser()
      .then((user) => {
        if (cancelled) return;
        setState({ isLoaded: true, isSignedIn: true, user });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ isLoaded: true, isSignedIn: false, user: null });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
